// DecompressionStream type — see ExcelUtils.d.ts or use global type augmentation
interface DecompressionStreamType {
  readonly readable: ReadableStream<Uint8Array>;
  readonly writable: WritableStream<Uint8Array>;
}

// ============================================================
// Public types
// ============================================================

export type CellValue = string | number | boolean | null;

export interface WorksheetRow {
  values: CellValue[];
}

export interface Worksheet {
  name: string;
  eachRow(
    options: { includeEmpty: boolean },
    callback: (row: WorksheetRow) => void
  ): void;
}

export interface Workbook {
  worksheets: Worksheet[];
  getWorksheet(name: string): Worksheet | undefined;
}

// ============================================================
// ZIP reader (browser-native DecompressionStream)
// ============================================================

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const ds = new (window as any).DecompressionStream('deflate-raw') as DecompressionStreamType;
  const writer = ds.writable.getWriter();
  writer.write(data);
  writer.close();

  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }

  const totalLength = chunks.reduce(function (sum, c) { return sum + c.length; }, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (let i = 0; i < chunks.length; i++) {
    result.set(chunks[i], offset);
    offset += chunks[i].length;
  }
  return result;
}

async function readZipEntries(buffer: ArrayBuffer): Promise<Map<string, Uint8Array>> {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  const entries = new Map<string, Uint8Array>();
  const decoder = new TextDecoder();

  // Find End of Central Directory record (search backwards)
  let eocdOffset = -1;
  for (let i = view.byteLength - 22; i >= 0; i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocdOffset = i;
      break;
    }
  }
  if (eocdOffset === -1) {
    throw new Error('Invalid ZIP file: End of Central Directory not found');
  }

  const centralDirOffset = view.getUint32(eocdOffset + 16, true);
  const entryCount = view.getUint16(eocdOffset + 10, true);

  let offset = centralDirOffset;

  for (let i = 0; i < entryCount; i++) {
    if (view.getUint32(offset, true) !== 0x02014b50) break;

    const compressionMethod = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const filenameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);

    const filename = decoder.decode(bytes.slice(offset + 46, offset + 46 + filenameLength));

    // Navigate to local file header to get actual data position
    const localFilenameLength = view.getUint16(localHeaderOffset + 26, true);
    const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
    const dataOffset = localHeaderOffset + 30 + localFilenameLength + localExtraLength;

    const compressedData = bytes.slice(dataOffset, dataOffset + compressedSize);

    if (compressionMethod === 0) {
      // Stored (no compression)
      entries.set(filename, compressedData);
    } else if (compressionMethod === 8) {
      // Deflate
      entries.set(filename, await inflateRaw(compressedData));
    }

    offset += 46 + filenameLength + extraLength + commentLength;
  }

  return entries;
}

// ============================================================
// XLSX parser (browser-native DOMParser)
// ============================================================

const NS_SPREADSHEET = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const NS_RELATIONSHIPS = 'http://schemas.openxmlformats.org/package/2006/relationships';
const NS_REL_OFFICEDOC = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

function getElementsByLocal(parent: Document | Element, localName: string): Element[] {
  // getElementsByTagNameNS with wildcard namespace matches any namespace
  const collection = parent.getElementsByTagNameNS('*', localName);
  const result: Element[] = [];
  for (let i = 0; i < collection.length; i++) {
    result.push(collection[i]);
  }
  return result;
}

function getTextFromSi(el: Element): string {
  // Handle <si> elements which may contain rich text runs: <r><t>text</t></r>
  const tElements = el.getElementsByTagNameNS('*', 't');
  if (tElements.length > 0) {
    let text = '';
    for (let i = 0; i < tElements.length; i++) {
      text += tElements[i].textContent || '';
    }
    return text;
  }
  return el.textContent || '';
}

function parseSharedStrings(xml: string): string[] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const strings: string[] = [];
  const siElements = getElementsByLocal(doc, 'si');
  for (let i = 0; i < siElements.length; i++) {
    strings.push(getTextFromSi(siElements[i]));
  }
  return strings;
}

interface SheetInfo {
  name: string;
  path: string;
}

function parseWorkbookInfo(workbookXml: string, relsXml: string): SheetInfo[] {
  const parser = new DOMParser();

  // Parse relationships
  const relsDoc = parser.parseFromString(relsXml, 'application/xml');
  const relMap = new Map<string, string>();
  const relElements = getElementsByLocal(relsDoc, 'Relationship');
  for (let i = 0; i < relElements.length; i++) {
    const el = relElements[i];
    relMap.set(el.getAttribute('Id') || '', el.getAttribute('Target') || '');
  }

  // Parse workbook sheets
  const wbDoc = parser.parseFromString(workbookXml, 'application/xml');
  const sheetElements = getElementsByLocal(wbDoc, 'sheet');
  const sheets: SheetInfo[] = [];

  for (let i = 0; i < sheetElements.length; i++) {
    const el = sheetElements[i];
    const name = el.getAttribute('name') || '';
    // r:id is a namespaced attribute
    const rId = el.getAttributeNS(NS_REL_OFFICEDOC, 'id') || el.getAttribute('r:id') || '';
    const target = relMap.get(rId) || '';
    const path = target.startsWith('/') ? target.slice(1) : 'xl/' + target;
    sheets.push({ name, path });
  }

  return sheets;
}

function columnLetterToIndex(col: string): number {
  let index = 0;
  for (let i = 0; i < col.length; i++) {
    index = index * 26 + (col.charCodeAt(i) - 64); // A=1, B=2, ...
  }
  return index;
}

function parseCellRef(ref: string): { col: number; row: number } {
  const match = ref.match(/^([A-Z]+)(\d+)$/);
  if (!match) return { col: 1, row: 1 };
  return { col: columnLetterToIndex(match[1]), row: parseInt(match[2], 10) };
}

// Excel epoch: Jan 1, 1900 (with the Lotus 1-2-3 leap year bug)
function excelSerialToDate(serial: number): string {
  // Excel incorrectly treats 1900 as a leap year; serials > 59 are off by one day
  const excelEpoch = Date.UTC(1899, 11, 30); // Dec 30, 1899
  const ms = excelEpoch + serial * 86400000;
  const d = new Date(ms);
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  const year = d.getUTCFullYear();
  return month + '/' + day + '/' + year;
}

// Built-in Excel number format IDs that represent dates/times
const BUILTIN_DATE_FORMAT_IDS = new Set([
  14, 15, 16, 17, 18, 19, 20, 21, 22, 45, 46, 47
]);

function isDateFormatString(fmt: string): boolean {
  // Remove escaped characters and quoted strings
  const stripped = fmt.replace(/\\./g, '').replace(/"[^"]*"/g, '').replace(/\[.*?\]/g, '');
  // Check for date/time tokens
  return /[yYmMdDhHsS]/.test(stripped) && !/[0#]/.test(stripped);
}

// Built-in Excel number format IDs that represent percentages
const BUILTIN_PERCENTAGE_FORMAT_IDS = new Set([9, 10]);

function isPercentageFormatString(fmt: string): boolean {
  const stripped = fmt.replace(/\\./g, '').replace(/"[^"]*"/g, '').replace(/\[.*?\]/g, '');
  return stripped.includes('%');
}

function parseDateFormatIds(stylesXml: string): { dateIds: Set<number>; percentageIds: Set<number> } {
  const doc = new DOMParser().parseFromString(stylesXml, 'application/xml');
  const dateIds = new Set<number>();
  const percentageIds = new Set<number>();

  // Collect custom date and percentage formats from numFmts
  const customDateFmtIds = new Set<number>();
  const customPercentageFmtIds = new Set<number>();
  const numFmtElements = getElementsByLocal(doc, 'numFmt');
  for (let i = 0; i < numFmtElements.length; i++) {
    const id = parseInt(numFmtElements[i].getAttribute('numFmtId') || '0', 10);
    const code = numFmtElements[i].getAttribute('formatCode') || '';
    if (isDateFormatString(code)) {
      customDateFmtIds.add(id);
    }
    if (isPercentageFormatString(code)) {
      customPercentageFmtIds.add(id);
    }
  }

  // Parse cellXfs to map style index → numFmtId
  const cellXfsEl = getElementsByLocal(doc, 'cellXfs')[0];
  if (cellXfsEl) {
    const cellXfElements = cellXfsEl.getElementsByTagNameNS('*', 'xf');
    for (let i = 0; i < cellXfElements.length; i++) {
      const numFmtId = parseInt(cellXfElements[i].getAttribute('numFmtId') || '0', 10);
      if (BUILTIN_DATE_FORMAT_IDS.has(numFmtId) || customDateFmtIds.has(numFmtId)) {
        dateIds.add(i);
      }
      if (BUILTIN_PERCENTAGE_FORMAT_IDS.has(numFmtId) || customPercentageFmtIds.has(numFmtId)) {
        percentageIds.add(i);
      }
    }
  }

  return { dateIds, percentageIds };
}

function parseWorksheetXml(xml: string, sharedStrings: string[], dateFormatIds: Set<number>, percentageFormatIds: Set<number>): CellValue[][] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const rows: CellValue[][] = [];

  const rowElements = getElementsByLocal(doc, 'row');

  for (let ri = 0; ri < rowElements.length; ri++) {
    const rowEl = rowElements[ri];
    const rowNum = parseInt(rowEl.getAttribute('r') || '0', 10);

    // Expand rows array to include empty rows up to this row number
    while (rows.length < rowNum) {
      rows.push([]);
    }

    const rowData: CellValue[] = [];
    const cellElements = rowEl.getElementsByTagNameNS('*', 'c');

    for (let ci = 0; ci < cellElements.length; ci++) {
      const cellEl = cellElements[ci];
      const ref = cellEl.getAttribute('r') || '';
      const type = cellEl.getAttribute('t') || '';
      const styleIndex = parseInt(cellEl.getAttribute('s') || '0', 10);
      const { col } = parseCellRef(ref);

      // Pad with nulls up to this column
      while (rowData.length < col) {
        rowData.push(null);
      }

      const vEl = cellEl.getElementsByTagNameNS('*', 'v')[0];
      const rawValue = vEl ? (vEl.textContent || '') : '';

      let value: CellValue;

      if (type === 's') {
        // Shared string reference
        value = sharedStrings[parseInt(rawValue, 10)] || '';
      } else if (type === 'b') {
        value = rawValue === '1';
      } else if (type === 'inlineStr') {
        const isEl = cellEl.getElementsByTagNameNS('*', 'is')[0];
        value = isEl ? getTextFromSi(isEl) : '';
      } else if (type === 'str' || type === 'e') {
        value = rawValue;
      } else {
        // Number or default
        if (rawValue === '') {
          value = null;
        } else {
          const num = Number(rawValue);
          if (isNaN(num)) {
            value = rawValue;
          } else if (dateFormatIds.has(styleIndex)) {
            // Date serial number — convert to MM/DD/YYYY
            value = excelSerialToDate(num);
          } else if (percentageFormatIds.has(styleIndex)) {
            // Percentage — convert to "X%" string (e.g. 0.18 → "18%")
            const pct = Math.round(num * 100 * 100) / 100;
            value = (pct % 1 === 0 ? String(Math.round(pct)) : String(pct)) + '%';
          } else {
            value = num;
          }
        }
      }

      rowData[col - 1] = value; // store 0-based
    }

    rows[rowNum - 1] = rowData;
  }

  return rows;
}

function createWorksheet(name: string, data: CellValue[][]): Worksheet {
  return {
    name,
    eachRow(
      options: { includeEmpty: boolean },
      callback: (row: WorksheetRow) => void
    ) {
      for (let i = 0; i < data.length; i++) {
        const rowData = data[i];
        if (!options.includeEmpty && (!rowData || rowData.length === 0)) {
          continue;
        }
        // Match ExcelJS convention: row.values is 1-indexed (index 0 is null)
        const values: CellValue[] = ([null] as CellValue[]).concat(rowData || []);
        callback({ values });
      }
    }
  };
}

// ============================================================
// Public API — Excel
// ============================================================

/**
 * Load an .xlsx workbook from an ArrayBuffer (zero external dependencies).
 */
export const loadWorkbook = async (buffer: ArrayBuffer): Promise<Workbook> => {
  const zipEntries = await readZipEntries(buffer);
  const decoder = new TextDecoder();

  const getEntry = (path: string): string | null => {
    const data = zipEntries.get(path);
    return data ? decoder.decode(data) : null;
  };

  // Parse shared strings
  const sharedStringsXml = getEntry('xl/sharedStrings.xml');
  const sharedStrings = sharedStringsXml ? parseSharedStrings(sharedStringsXml) : [];

  // Parse styles to detect date and percentage formatted cells
  const stylesXml = getEntry('xl/styles.xml');
  const { dateIds: dateFormatIds, percentageIds: percentageFormatIds } = stylesXml
    ? parseDateFormatIds(stylesXml)
    : { dateIds: new Set<number>(), percentageIds: new Set<number>() };

  // Parse workbook structure
  const workbookXml = getEntry('xl/workbook.xml');
  const relsXml = getEntry('xl/_rels/workbook.xml.rels');

  if (!workbookXml || !relsXml) {
    throw new Error('Invalid XLSX file: missing workbook.xml or relationships');
  }

  const sheetsInfo = parseWorkbookInfo(workbookXml, relsXml);

  // Build worksheets
  const worksheets: Worksheet[] = [];
  for (let i = 0; i < sheetsInfo.length; i++) {
    const sheetXml = getEntry(sheetsInfo[i].path);
    if (sheetXml) {
      worksheets.push(
        createWorksheet(sheetsInfo[i].name, parseWorksheetXml(sheetXml, sharedStrings, dateFormatIds, percentageFormatIds))
      );
    }
  }

  return {
    worksheets,
    getWorksheet(name: string): Worksheet | undefined {
      return worksheets.find(function (ws) { return ws.name === name; });
    }
  };
};

/**
 * Convert a Worksheet to a CSV string.
 * @param forceStringQuoting When true, all non-empty cell values are wrapped in double quotes,
 *   ensuring they are treated as strings (e.g. preserving leading zeros or numeric-looking values).
 */
export const sheetToCsv = (worksheet: Worksheet, forceStringQuoting = false): string => {
  const csvRows: string[] = [];
  worksheet.eachRow({ includeEmpty: true }, (row: WorksheetRow) => {
    const values = row.values;
    // row.values is 1-indexed (index 0 is null), so skip index 0
    const cells = values.slice(1).map(function (v) {
      if (v === null || v === undefined) return '';
      const str = String(v);
      // With forceStringQuoting, only quote numeric values (to prevent CSV parsers treating
      // them as numbers). String values like "18%" don't need extra quoting.
      const needsQuoting = (forceStringQuoting && typeof v === 'number')
        || str.includes(',') || str.includes('"') || str.includes('\n');
      if (needsQuoting) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    });
    csvRows.push(cells.join(','));
  });
  return csvRows.join('\n');
};

// ============================================================
// Public API — CSV
// ============================================================

/**
 * Create a File object from a CSV string.
 */
export const createCsvFile = (csvContent: string, fileName: string): File => {
  const blob = new Blob([csvContent], { type: 'text/csv' });
  return new File([blob], fileName, { type: 'text/csv' });
};

/**
 * Parse a CSV line handling quoted fields.
 */
export const parseCSVLine = (line: string): string[] => {
  const fields: string[] = [];
  let currentField = '';
  let withinQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === ',' && !withinQuotes) {
      fields.push(currentField.trim());
      currentField = '';
    } else if (char === '"') {
      withinQuotes = !withinQuotes;
    } else {
      currentField += char;
    }
  }

  fields.push(currentField.trim());

  return fields;
};

/**
 * Parse CSV data into an array of key-value objects using the first row as headers.
 */
export const parseCSV = (csvData: string): Record<string, string>[] => {
  const lines = csvData.split(/\r\n|\n/);
  const headers = lines[0].split(',');

  const jsonData: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const fields = parseCSVLine(lines[i]);
    const obj: Record<string, string> = {};

    for (let j = 0; j < headers.length; j++) {
      obj[headers[j]] = fields[j];
    }

    jsonData.push(obj);
  }

  return jsonData;
};
